// NPM
import React, { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { isNil, pick, isEmpty, isString } from 'lodash';
import { Helmet } from "react-helmet";

// ATOM
import { paymentStatusAtom } from 'Recoil/payment.atom';

// SERVICE
import { fetchPaymentObject, FetchPaymentObjectResponse } from 'Services/payment.services';

// HELPER
import { decode, encode, setWithExpiry } from 'Helpers/index';

// HOOK
import { useSession, useQuery } from 'Hooks/index';
import { LocalCartAtom } from 'Recoil/order.atom';

// ANT COMPONENTS
import { Space } from 'antd';
import { Loader } from '@life-line-lab/components-react';

// STYLE
import './payment-prebuilt.scss';
import { useHistory } from 'react-router-dom';

const PaymentPrebuiltPage = () => {
  const { profile } = useSession();
  const { location } = useHistory<any>();
  const query = useQuery();
  const ref = query.get('ref');
  const paymentType = query.get('type');
  const orderIDs: string[] | null = ref ? JSON.parse(decode(ref)) : null;

  const isRevPay = !isNil(orderIDs) && !isNil(paymentType);
  const isKiplePay = !isNil(orderIDs) && isNil(paymentType);

  const [paymentStatus, setPaymentStatus] = useRecoilState<any>(paymentStatusAtom);
  const [localCartState, setLocalCartState] = useRecoilState<any>(LocalCartAtom);
  const [isSubmited, setIsSubmitted] = useState<boolean>(false);
  const [input, setInput] = useState<any>({});

  useEffect(() => {
    fetch()
  }, [])

  useEffect(() => {
    // Signature for revpay
    // merchant_hashvalue for kiplepay
    if ((input.Signature || input.merchant_hashvalue) && !isSubmited) {
      // console.log({ Signature: input.Signature, merchant_hashvalue: input.merchant_hashvalue });
      // console.log({
      //   input: pick(input, [
      //     'Revpay_Merchant_ID',
      //     'Payment_ID',
      //     'Reference_Number',
      //     'Amount',
      //     'Currency',
      //     'Transaction_Description',
      //     'Customer_IP',
      //     'Key_Index',
      //     'Signature',
      //     'Return_URL'
      //   ])
      // });
      handleSubmit();
    }
  }, [input.Signature, input.merchant_hashvalue, isSubmited])

  const fetch = async () => {
    setPaymentStatus({ ...paymentStatus, submit: 'loading' });

    let data: any;

    if (location.state?.isRepayment) {
      // * from payment.service (createRePaymentObject()), pass from 'purchase-list' component
      data = location.state.data;
    } else {
      data = await fetchPaymentObject({
        orderIDs: !isEmpty(orderIDs) ? orderIDs : null,
        shipName: profile.fullName,
        email: profile.primaryEmail,
        phone: profile.primaryPhone.fullNumber,
        appliedPlatformVouchers: localCartState?.appliedPlatformVouchers,
        paymentBetween: "USER_PLATFORM",
        revpay_payment_code: !isNil(paymentType) ? parseInt(paymentType) : undefined
      });
    }

    setLocalCartState({...localCartState, appliedPlatformVouchers:[]})

    if (!isNil(data)) {
      const ttl = 15 * 60 * 1000;
      const key = process.env.AWAITING_PAYMENT_ORDER_ID;

      setWithExpiry(key, { paymentID: data.paymentID }, ttl) // Set 15 minutes intervals
      setPaymentStatus({ ...paymentStatus, submit: 'success' });
      setInput({
        ...input,
        ...data.payload,
        Return_URL: `${process.env.GATEWAY_RETURN_URL}?ref=${encode(data.paymentID)}`
      });
    }
    else {
      setPaymentStatus({ ...paymentStatus, submit: 'error' });
    }
  }

  const handleSubmit = () => {
    setIsSubmitted(true);

    const form = document.getElementById('submitForm');
    (form as HTMLFormElement).submit();
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>
          Payment
        </title>
      </Helmet>
      <div className="payment-prebuilt-page">
        <Space direction="vertical" size="large" className="center">
          <div style={{ marginTop: '100px' }}>
            <h3 className="title">
              Please wait while we redirect you to the payment site...
            </h3>
          </div>

          <Loader type="spinner" size="large" />
        </Space>

        <div style={{ opacity: '0' }}>
          <form id="submitForm" action={process.env.GATEWAY_SUBMIT_URL} method="post">
            {isRevPay &&
              <>
                {/* Mandatory RevPay */}
                <input type="hidden" name="Revpay_Merchant_ID" value={input.Revpay_Merchant_ID} />
                <input type="hidden" name="Payment_ID" value={input.Payment_ID} />
                <input type="hidden" name="Reference_Number" value={input.Reference_Number} />
                <input type="hidden" name="Amount" value={input.Amount} />
                <input type="hidden" name="Currency" value={input.Currency} />
                <input type="hidden" name="Transaction_Description" value={input.Transaction_Description} />
                <input type="hidden" name="Customer_IP" value={input.Customer_IP} />
                <input type="hidden" name="Key_Index" value={input.Key_Index} />
                <input type="hidden" name="Signature" value={input.Signature} />
                <input type="hidden" name="Return_URL" value={input.Return_URL} />
              </>
            }

            {isKiplePay &&
              <>
                <input type="hidden" name="dynamic_callback_url" value={input.dynamic_callback_url} />
                <input type="hidden" name="hash_type" value={input.hash_type} />
                <input type="hidden" name="merchant_hashvalue" value={input.merchant_hashvalue} />
                <input type="hidden" name="ord_customfield1" value={input.ord_customfield1} />
                <input type="hidden" name="ord_customfield2" value={input.ord_customfield2} />
                <input type="hidden" name="ord_customfield3" value={input.ord_customfield3} />
                <input type="hidden" name="ord_date" value={input.ord_date} />
                <input type="hidden" name="ord_email" value={input.ord_email} />
                <input type="hidden" name="ord_gstamt" value={input.ord_gstamt} />
                <input type="hidden" name="ord_mercID" value={input.ord_mercID} />
                <input type="hidden" name="ord_mercref" value={input.ord_mercref} />
                <input type="hidden" name="ord_returnURL" value={`${input.Return_URL}`} />
                <input type="hidden" name="ord_shipname" value={input.ord_shipname} />
                <input type="hidden" name="ord_telephone" value={input.ord_telephone} />
                <input type="hidden" name="ord_totalamt" value={input.ord_totalamt} />
                <input type="hidden" name="version" value={input.version} />
              </>
            }
          </form>
        </div>
      </div>
    </React.Fragment>
  )
}

export default PaymentPrebuiltPage;

{/* <form id="submitForm" action={process.env.GATEWAY_SUBMIT_URL} method="post">
Mandatory RevPay
<input type="hidden" name="Revpay_Merchant_ID" value={input.Revpay_Merchant_ID} />
<input type="hidden" name="Payment_ID" value={input.Payment_ID} />
<input type="hidden" name="Reference_Number" value={input.Reference_Number} />
<input type="hidden" name="Amount" value={input.Amount} />
<input type="hidden" name="Currency" value={input.Currency} />
<input type="hidden" name="Transaction_Description" value={input.Transaction_Description} />
<input type="hidden" name="Customer_IP" value={input.Customer_IP} />
<input type="hidden" name="Key_Index" value={input.Key_Index} />
<input type="hidden" name="Signature" value={input.Signature} />
<input type="hidden" name="Return_URL" value={`${process.env.GATEWAY_RETURN_URL}?ref=${encode(paymentID)}`} />
<input type="hidden" name="Return_URL" value={input.Return_URL} /> */}

{/* Optional RevPay PS: Can be empty but the field must exist according to the documentation */}
{/* <input type="hidden" name="Bank_Code" value={input.Bank_Code} />
<input type="hidden" name="Billing_Address" value={input.Billing_Address} /> */}
{/* <input type="hidden" name="Shipping_Address" value={'No 24, Jalan Sari Ilmia, 43000, Kajang Selangor'} /> */}
{/* <input type="hidden" name="Ecomm_Marketplace" value={input.Ecomm_Marketplace} />
<input type="hidden" name="Promo_Code" value={input.Promo_Code} /> */}
{/* <input type="hidden" name="Customer_Name" value={input.Customer_Name} />
<input type="hidden" name="Customer_Email" value={input.Customer_Email} />
<input type="hidden" name="Customer_Contact" value={input.Customer_Contact} /> */}
{/* <input type="hidden" name="Geo_Location" value={input.Geo_Location} /> */}

{/* Conditiona RevPay */}
{/* <input type="hidden" name="Device_ID" value={input.Device_ID} />
<input type="hidden" name="Transaction_Type" value={input.Transaction_Type} />
<input type="hidden" name="Customer_ID" value={input.Customer_ID} />
<input type="hidden" name="Card_Type" value={input.Card_Type} />
<input type="hidden" name="Card_Holder_Name" value={input.Card_Holder_Name} />
<input type="hidden" name="Funding_Exp_Date" value={input.Funding_Exp_Date} />
<input type="hidden" name="Funding_CVV" value={input.Funding_CVV} />
<input type="hidden" name="Card_Issuer_Bank_Country_Code" value={input.Card_Issuer_Bank_Country_Code} />
<input type="hidden" name="Installment_Plan" value={input.Installment_Plan} />
<input type="hidden" name="Installment_Term" value={input.Installment_Term} />
<input type="hidden" name="Token_Pan" value={input.Token_Pan} />
<input type="hidden" name="Token_Exp_Date" value={input.Token_Exp_Date} /> */}

{/** This one for KipplePay */}
{/* <input type="hidden" name="dynamic_callback_url" value={input.dynamic_callback_url} />
<input type="hidden" name="hash_type" value={input.hash_type} />
<input type="hidden" name="merchant_hashvalue" value={input.merchant_hashvalue} />
<input type="hidden" name="ord_customfield1" value={input.ord_customfield1} />
<input type="hidden" name="ord_customfield2" value={input.ord_customfield2} />
<input type="hidden" name="ord_customfield3" value={input.ord_customfield3} />
<input type="hidden" name="ord_date" value={input.ord_date} />
<input type="hidden" name="ord_email" value={input.ord_email} />
<input type="hidden" name="ord_gstamt" value={input.ord_gstamt} />
<input type="hidden" name="ord_mercID" value={input.ord_mercID} />
<input type="hidden" name="ord_mercref" value={input.ord_mercref} />
<input type="hidden" name="ord_returnURL" value={`${process.env.GATEWAY_RETURN_URL}?ref=${paymentID}`} />
<input type="hidden" name="ord_shipname" value={input.ord_shipname} />
<input type="hidden" name="ord_telephone" value={input.ord_telephone} />
<input type="hidden" name="ord_totalamt" value={input.ord_totalamt} />
<input type="hidden" name="version" value={input.version} /> */}
{/* </form> */}
